import * as vis from 'vis-network/standalone/umd/vis-network'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Tooltip, Row, Col, Tag, Button, Layout } from 'antd'
import {
  CreateRelationModal
  RenameRelationModal
  SearchGraphButton
} from 'Components'
import GraphColorPicker from './GraphColorPicker'
import GraphLayout from './GraphLayout'
import GraphLegend from './GraphLegend'
import GraphShowNodePopover from './GraphShowNodePopover'
import GraphFilterButton from './GraphFilterButton'
import { setFilteredNodesBySearch, setHideRelationLabels, setShowRelationsAsNodes } from 'Actions/graphFilter'
import { setGraphAddingEdge, setGraphCreatingEdge, setGraphNodes, setGraphNodeView, setSelectedGraphItems, updateGraphNodes } from 'Actions/graph'
import { getColorByGraph, getColorByType, createRelationInGraph, toggleRelationNodes, toggleRelationLabels } from 'Services/GraphUtils'
import { getNodeLabel, getMemberKey, getTypeForNode, getRelationkeyLabel } from 'Services/WeaverUtils'
import Feather from '@sysunite/react-feather'
import {
  filterNodesByGraph
  filterNodesByRelation
  filterNodesByType
  filterRelationsByKey
  flatMapUnique
} from 'Services/GraphFilterUtils'
import { NODE, RELATION, RELATION_NODE } from 'Types'
import styled from 'styled-components'
import ShortcutManager from 'Services/ShortcutManager'
import './Graph.scss'

export class Graph extends React.Component
  defaultOptions = (darkMode) ->
    physics:
      stabilization: false
      solver: 'forceAtlas2Based'
      forceAtlas2Based:
        theta: 0.75
        damping: 1
    autoResize: true
    layout:
      hierarchical: false
    interaction:
      hover: true
      multiselect: true
    nodes:
      shape: 'dot',
      size: 10,
      borderWidth : 2
      font:
        face: 'Raleway'
        size: 12
        color: darkMode && '#bbc4d4' || undefined
        strokeWidth: 1
        strokeColor: darkMode && '#000' || undefined
    edges:
      smooth: true
      arrowStrikethrough: false
      color:
        inherit: 'both'
      font:
        color: darkMode && '#dae3ed' || 'rgb(0,0,0)'
        face: 'Avenir'
        size: 10
        strokeColor: darkMode && 'rgb(6,6,6)' || '#fff'
      arrows:
        to:
          enabled: true
          scaleFactor: 0.5

  constructor: (props) ->
    super(props)
    @container = React.createRef()
    { data: { nodes, relNodes, edges }, contextMenuCb } = @props

    nodeSet = new vis.DataSet(_.values(nodes))
    edgeSet = new vis.DataSet(_.values(edges))

    @internalEvents =
      hoverEdge: ({ edge }) =>
        @setEdgeLabelVisility(edge, true)
      blurEdge: ({edge}) =>
        @setEdgeLabelVisility(edge, false)
      click: =>
        @props.setGraphCreatingEdge(undefined)
      controlNodeDragEnd: ({ controlEdge, pointer: { DOM }}) =>
        if controlEdge.to?
          @props.setGraphCreatingEdge({
            @props.creatingEdgeProps...
            x: DOM.x,
            y: DOM.y
          })
      oncontext: ({ event, pointer, nodes, edges }) =>
        target = undefined
        event.preventDefault()
        if contextMenuCb
          { x, y } = pointer.DOM
          node = @network.getNodeAt({ x, y })
          edge = @network.getEdgeAt({ x, y })

          if node? and node not in nodes
            nodes = [node]
            edges = []

          if (edge? and edge not in edges) and not node?
            edges = [edge]
            nodes = []

          @network.setSelection({
            nodes
            edges
          }, {
            highlightEdges: false
          })

          if node?
            if nodeSet.get(node).type is RELATION_NODE
              target = RELATION
            else
              target = NODE
          else if edge?
            target = RELATION

          contextMenuCb({ event, pointer, nodes, edges, target, network: @network })

    @state = {
      identifier: 'graph-container'
      isFrozen: false
      relationFilter: {}
      edgeView: undefined
      nodeView: undefined
      nodeSet
      edgeSet
      shouldHideOnFilter: localStorage.getItem('shouldHideOnFilter') is 'true'
      hideRelationLabels: localStorage.getItem('hideRelationLabels') is 'true'
      nodesToHide: []
      relationsToHide: []
    }

  componentDidMount: ->
    { dispatch, darkModeEnabled, events, contextMenuCb, data: { groups }, options, selectedGraphItems } = @props
    { nodeSet, edgeSet } = @state

    opts = defaultOptions(darkModeEnabled)
    opts.groups = groups

    @applyFilter()

    edgeView = new vis.DataView(edgeSet, {
      filter: (edge) =>
        return true if @state.shouldHideOnFilter
        { relationsToHide } = @state
        edge.id not in relationsToHide
    })

    nodeView = new vis.DataView(nodeSet, {
      filter: (node) =>
        return true if @state.shouldHideOnFilter
        node.id not in @state.nodesToHide
    })

    mergedOptions = _.merge(opts, options)

    mergedOptions = {
      mergedOptions...
      manipulation:
        enabled: false
        addEdge: (edgeData, callback) =>
          { hideRelationLabels } = @state
          { data: { nodes }} = @props
          if edgeData.to?
            promise = new Promise((resolve, reject) =>
              @props.setGraphCreatingEdge({
                edgeData
                @props.creatingEdgeProps...
                promise: {
                  resolve
                  reject
                }
              })
            )

            { sources, relKeys, targets } = await promise

            @props.updateGraphNodes(await createRelationInGraph(relKeys, sources, targets))
            @props.setGraphCreatingEdge(undefined)

          setTimeout(=>
            @network.addEdgeMode()
          )
    }

    @network = new vis.Network(@container.current, { nodes: nodeView, edges: edgeView }, mergedOptions)

    @network.on('startStabilizing', =>
      @setState({ isStabilizing: true })
    )

    @network.on('stabilized', =>
      @setState({ isStabilizing: false })
    )

    for key, value of events
      @network.on(key, value)

    for key, value of @internalEvents
      @network.on(key, value)

    @setState({ edgeView }, =>
      @network.fit()
    )
    @network.setSelection(selectedGraphItems) if selectedGraphItems isnt undefined
    @props.setGraphNodeView(nodeView)

  componentDidUpdate: (prevProps, prevState) ->

    {
      edgeSet
      hideRelationLabels
      nodeSet
      showRelationsAsNodes
      colorByGraph
    } = @state

    {
      data: {
        edges
        groups
        nodes
        relNodes
      }
      dispatch
      events
      filteredNodes
      graphFilters
      isAddingEdge
      relationFilters
      filteredRelations
      tabIsLocked
      typeFilters
    } = @props

    for key, value of prevProps.events
      @network.off(key, value)

    for key, value of events
      @network.on(key, value)

    nodesAndEdgesChanged = not _.isEqual(prevProps.data.nodes, nodes) or
      not _.isEqual(prevProps.data.edges, edges)

    groupsChanged = not _.isEqual(prevProps.data.groups, groups)

    if not _.isEqual(prevProps.typeFilters, typeFilters) or nodesAndEdgesChanged
      dispatch(await filterNodesByType())

    if not _.isEqual(prevProps.relationFilters, relationFilters) or nodesAndEdgesChanged
      dispatch(await filterNodesByRelation())
      dispatch(await filterRelationsByKey())

    if not _.isEqual(prevProps.graphFilters, graphFilters) or nodesAndEdgesChanged
      dispatch(await filterNodesByGraph())

    if (
      not _.isEqual(prevProps.filteredNodes, filteredNodes) or
      not _.isEqual(prevProps.filteredRelations, filteredRelations)
    )
      @applyFilter()

    if groupsChanged
      @network.setOptions({ groups })

    if nodesAndEdgesChanged
      @network.releaseNode()
      nodeSet.remove(_.difference(_.keys(prevProps.data.nodes), _.keys(nodes)))
      nodeSet.update(_.values(nodes))
      edgeSet.remove(_.difference(_.keys(prevProps.data.edges), _.keys(edges)))
      edgeSet.update(_.values(edges))
      @refreshViews()


    if prevProps.isAddingEdge isnt isAddingEdge
      if isAddingEdge
        @setState({cleanup: ShortcutManager.register(
          'esc'
          , =>
            @props.setGraphAddingEdge(false)
          , true
        )})
        @network.addEdgeMode()
      else
        @state.cleanup?()
        @network.disableEditMode()

    if prevState.showRelationsAsNodes isnt showRelationsAsNodes
      @props.setShowRelationsAsNodes(showRelationsAsNodes)
      @props.setGraphNodes(await toggleRelationNodes(nodes, relNodes, edges, groups, showRelationsAsNodes))

    if prevState.hideRelationLabels isnt hideRelationLabels
      @props.setHideRelationLabels(hideRelationLabels)
      @props.updateGraphNodes(toggleRelationLabels(edges, hideRelationLabels))

    if prevState.colorByGraph isnt colorByGraph
      if colorByGraph
        @props.updateGraphNodes(getColorByGraph(nodes))
      else
        @props.updateGraphNodes(getColorByType(nodes))

  componentWillUnmount: ->
    @props.setSelectedGraphItems(undefined)
    @network.destroy()

  setEdgeLabelVisility: (id, visible) ->
    if @state.hideRelationLabels
      @props.updateGraphNodes(toggleRelationLabels({ [id]: @props.data.edges[id] }, !visible))

  refreshViews: ->
    { nodeView } = @props
    { edgeView } = @state
    nodeView.refresh()
    edgeView.refresh()
    @forceUpdate()

  applyFilter: ->
    { edgeSet, nodeSet, shouldHideOnFilter } = @state
    { data: { nodes, edges }, filteredRelations, filteredNodes } = @props

    nodesToHide = flatMapUnique(filteredNodes)
    relationsToHide = flatMapUnique(filteredRelations)

    if shouldHideOnFilter
      newNodes = _.map(nodes, (node) ->
        {
          node...
          hidden: node.id in nodesToHide
        }
      )

      newEdges = _.map(edges, (edge) ->
        {
          edge...
          hidden: edge.id in relationsToHide
        }
      )

      edgeSet.update(newEdges)
      nodeSet.update(newNodes)
    else
      @setState({ nodesToHide, relationsToHide }, =>
        @refreshViews()
      )

  onChangeFilter: (key, val) =>
    @setState({ [key]: val })

  highlightNodes: (misses) ->
    { data: { nodes } } = @props

    toHighlight = _.keys(nodes).filter((id) ->
      misses.indexOf(id) is -1
    )
    if toHighlight.length isnt 1
      @network.view.fit({ nodes: toHighlight, animation: true })
    else
      @network.view.focus(toHighlight[0], { scale: 2, locked: true, animation: true })

    if toHighlight.length isnt _.keys(nodes).length
      @network.selectNodes(toHighlight, false)
    else
      @network.unselectAll()

  onSearchGraph: (value, hideMisses) =>
    { data: { nodes } } = @props

    if value is '' then return @props.setFilteredNodesBySearch([])

    result = _.filter(nodes, (node) ->
      !(
        node.label.toLowerCase().includes(value.toLowerCase()) or
        node.id.toLowerCase().includes(value.toLowerCase())
      )
    ).map((node) -> node.id)

    if hideMisses
      @props.setFilteredNodesBySearch(result)
    else
      @props.setFilteredNodesBySearch([])
      @highlightNodes(result)

  freezeGraph: =>
    { isFrozen } = @state
    @setState({ isFrozen: !isFrozen }, =>
      { isFrozen } = @state
      { data: { nodes } } = @props
      @props.updateGraphNodes({ nodes: _.keyBy(_.map(nodes, (node) ->
        {
          node...
          physics: not isFrozen
        }
      ), 'id')})
    )

  render: ->
    {
      colorPickerProps,
      actions,
      creatingEdgeProps,
      renameRelationProps,
      isAddingEdge,
      isShowingNode,
      isEmbeddedView,
      data: { groups },
      nodeView
    } = @props
    { identifier, edgeView, edgeSet, nodeSet, isFrozen } = @state

    <GraphViewContainer style={@props.style}>
      <Row align="top" gutter={26}>
        <Col>
          <GraphFilterButton onChangeFilter={@onChangeFilter} />
        </Col>
        <Col>
          <SearchGraphButton onSearch={@onSearchGraph}/>
        </Col>
        <Col>
          <GraphLayout
            onSetHierarchical={(hierarchical) =>
              @network?.setOptions({
                layout: {
                  hierarchical
                }
              })
            } />
        </Col>
        <Col>
          <Tooltip placement="bottomLeft"
                  trigger="hover"
                  mouseEnterDelay={1}
                  title="Freeze/Unfreeze the graph">
            <Button
              onClick={@freezeGraph}
              type="link"
              icon={<Feather size="1.5em" type="pause" />}
              className={isFrozen && 'active'}
            />
          </Tooltip>
        </Col>
        {
          if isAddingEdge
            <Col style={{ pointerEvents: 'auto' }}>
              <Tag color="orange" closable onClose={=>
                @props.setGraphAddingEdge(false)
                @state.cleanup?()
              }>
                Creating relation
              </Tag>
            </Col>
        }
      </Row>

      <CreateRelationModal data={creatingEdgeProps} />
      <RenameRelationModal data={renameRelationProps} />

      {
        if isShowingNode?
          <GraphShowNodePopover event={isShowingNode.event} />
      }
      {
        if colorPickerProps?
          <GraphColorPicker {colorPickerProps...} />
      }

      {actions && actions.map((children, i) ->
        <span key={i} style={{ margin: '16px', marginLeft: (i+1) * 86, zIndex: '2', position: 'absolute', width: 54 }}>
          { children }
        </span>
      )}

      <div
        id={identifier}
        ref={@container}
        style={{
          cursor: if isAddingEdge then 'copy' else 'default'
          position: 'relative'
          width: '100%'
          height: '100vh'
        }}
      />
    </GraphViewContainer>

GraphViewContainer = styled(Layout)"""
  height: 100%;
  position: relative;
  background: transparent;
  & > .ant-row {
    pointer-events: none;
    margin: 16px !important;
    z-index: 2;
    position: absolute;
    & > .ant-col {
      pointer-events: auto;
      & > * {
        background: transparent;
      }
      & > .is-open {
        background: var(--background);
      }
    }
  }
""";

export default connect(
  ({
    explore: { darkModeEnabled }
    graph: {
      colorPickerProps,
      isAddingEdge,
      creatingEdgeProps,
      renameRelationProps,
      selectedGraphItems,
      isShowingNode,
      nodeView,
      isEmbeddedView
    },
    graphFilter: {
      filteredNodes
      graphFilters
      relationFilters
      typeFilters
      filteredRelations
    }
  }) ->
    {
      colorPickerProps
      darkModeEnabled
      filteredNodes
      filteredRelations
      graphFilters
      isAddingEdge
      creatingEdgeProps
      renameRelationProps
      isShowingNode
      nodeView
      relationFilters
      typeFilters
      selectedGraphItems
      isEmbeddedView
    }
, (dispatch) -> {
  dispatch
  bindActionCreators({
    setGraphAddingEdge
    setGraphCreatingEdge
    setGraphNodeView
    setGraphNodes
    setHideRelationLabels
    setSelectedGraphItems
    setShowRelationsAsNodes
    setFilteredNodesBySearch
    updateGraphNodes
    }, dispatch)...
})(Graph)
